import { createSelector } from 'reselect';
import { unique } from '@synergy/utils';

import { ATTRIBUTE_NO_VALUE } from '../models/constants/attribute-no-value';
import { articleDetailSelector } from './articles-selectors';

export interface AttributeOption {
    attributeId: number;
    name: string;
    options: { value: string; variantId: number; isFullMatch: boolean }[];
}

export const articleVariantsSelector = createSelector([articleDetailSelector], article => {
    if (!article) {
        return { variants: [], attributeOptions: [] };
    }
    const { variants } = article;

    // Some transformations to get all/unique/sorted attributes
    const variantAttributes = variants.flatMap(variant => variant.attributes ?? []);
    const uniqueAttributes = unique(variantAttributes, x => x.attributeId).sort(
        (a, b) => a.attributeId - b.attributeId
    );
    const attributeIds = uniqueAttributes.map(attribute => attribute.attributeId);
    const currentAttributeValues = uniqueAttributes.reduce((acc, attribute) => {
        acc[attribute.attributeId] =
            article.attributes.find(att => att.attributeId === attribute.attributeId)?.value ?? ATTRIBUTE_NO_VALUE;
        return acc;
    }, {} as Record<number, string>);

    // Simple variant array
    const simpleVariants = article.variants.map(variant => ({
        id: variant.id,
        attributes: attributeIds.reduce((acc, attributeId) => {
            acc[attributeId] =
                variant.attributes?.find(attribute => attribute.attributeId === attributeId)?.value ??
                ATTRIBUTE_NO_VALUE;
            return acc;
        }, {} as Record<number, string>),
    }));

    // Create Values for the selects
    const selectValues = variantAttributes
        .reduce((acc, attribute) => {
            if (!acc[attribute.attributeId]) {
                acc[attribute.attributeId] = {
                    attributeId: attribute.attributeId,
                    name: attribute.name,
                    options: [],
                };
            }
            acc[attribute.attributeId].options.push({ value: attribute.value, variantId: -1, isFullMatch: false });

            return acc;
        }, [] as AttributeOption[])
        .filter(att => !!att); // Because we assigned the attributeId as index, there might be empty slots. We remove them here

    // Fill in values for missing attributes and remove duplicates
    selectValues.forEach(attribute => {
        if (attribute.options.length !== article.variants.length) {
            attribute.options.push({ value: ATTRIBUTE_NO_VALUE, variantId: -1, isFullMatch: false });
        }
        // Sort and remove duplicates
        attribute.options = unique(attribute.options, x => x.value).sort((a, b) => a.value.localeCompare(b.value));
    });

    // Set best variant matches to selectValues
    selectValues.forEach(attribute => {
        attribute.options.forEach(value => {
            const matchingVariants = simpleVariants.filter(
                variant => variant.attributes[attribute.attributeId] === value.value
            );
            const bestMatch = matchingVariants.reduce(
                (acc, variant) => {
                    const score = Object.keys(variant.attributes).reduce((score, attributeId) => {
                        return currentAttributeValues[+attributeId] === variant.attributes[+attributeId]
                            ? score + 1
                            : score;
                    }, 0);

                    if (acc.score <= score) {
                        return { score, variantId: variant.id };
                    }

                    return acc;
                },
                { variantId: 0, score: 0 }
            );

            value.variantId = bestMatch.variantId;
            value.isFullMatch = bestMatch.score >= attributeIds.length - 1; // -1 because one attribute has to be different than the current attributes
        });
    });

    // Search variants with same attributes as current article to show in picker
    const variantsForVariantPicker = article.variants.filter(variant =>
        Object.keys(currentAttributeValues).every(
            key =>
                currentAttributeValues[+key] ===
                (variant.attributes?.find(att => att.attributeId === +key)?.value ?? ATTRIBUTE_NO_VALUE)
        )
    );

    return { attributeOptions: selectValues, variants: variantsForVariantPicker };
});
